import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import "./MotionMorph.css";

/* =========================================================
   CONFIGURATION
========================================================= */
const API_BASE_URL = "https://backend.whalesai.com";
const PROMPT_CHECK_ENDPOINT = `${API_BASE_URL}/api/v1/prompt-checker/check`;
const GENERATE_ENDPOINT = `${API_BASE_URL}/api/v1/motion-video-ai/create`;
const SESSION_ENDPOINT = `${API_BASE_URL}/api/v1/motion-video-ai`;
const ACTIVE_JOB_KEY = "active_motion_video_ai";
const MAX_IMAGES = 4;
const IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
];
const VIDEO_TYPES = [
    "video/mp4",
    "video/webm",
];
const RESOLUTIONS = [
    { value: "480p", label: "480p", tag: "SD", creditsPerSecond: 20 },
    { value: "720p", label: "720p", tag: "HD", creditsPerSecond: 46 },
    { value: "1080p", label: "1080p", tag: "FHD", creditsPerSecond: 52 },
];
const ASPECTS = [
    { value: "16:9", label: "Widescreen" },
    { value: "9:16", label: "Vertical" },
];
const DEFAULT_PROMPT_EXAMPLE = "@Image1 is Person 1, @Image2 is Person 2. In @Video1, replace Person 1 with @Image1 and Person 2 with @Image2.";

const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("aiUser") || ""}`,
});

const showAlert = (text, icon = "warning") =>
    swal({
        text,
        icon,
        buttons: "Ok",
        className: "modal_class_success",
    });
const getStoredJob = () => {
    try {
        return JSON.parse(localStorage.getItem(ACTIVE_JOB_KEY) || "null");
    } catch {
        return null;
    }
};


const generateVideoThumbnails = (file, count = 12) =>
    new Promise((resolve) => {
        const videoElement = document.createElement("video");
        const objectUrl = URL.createObjectURL(file);
        const thumbnails = [];
        videoElement.preload = "auto";
        videoElement.muted = true;
        videoElement.playsInline = true;
        videoElement.src = objectUrl;
        videoElement.onloadedmetadata = async () => {
            const total = videoElement.duration;
            if (!Number.isFinite(total) || total <= 0) {
                URL.revokeObjectURL(objectUrl);
                resolve([]);
                return;
            }
            const canvas = document.createElement("canvas");
            canvas.width = 120;
            canvas.height = 68;
            const context = canvas.getContext("2d");
            for (let i = 0; i < count; i++) {
                const time = Math.min(
                    (total * i) / count,
                    Math.max(0, total - 0.1)
                );

                const frame = await new Promise((done) => {
                    videoElement.onseeked = () => {
                        try {
                            context.drawImage(
                                videoElement,
                                0,
                                0,
                                canvas.width,
                                canvas.height
                            );
                            done(canvas.toDataURL("image/jpeg", 0.65));
                        } catch {
                            done(null);
                        }
                    };
                    videoElement.currentTime = time;
                });

                if (frame) {
                    thumbnails.push(frame);
                }
            }

            videoElement.removeAttribute("src");
            videoElement.load();
            URL.revokeObjectURL(objectUrl);

            resolve(thumbnails);
        };
        videoElement.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve([]);
        };
    });

/* =========================================================
   COMPONENT
========================================================= */

export default function MotionMorph() {
    const { AIuser, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [video, setVideo] = useState(null);
    const [prompt, setPrompt] = useState("");
    const [duration, setDuration] = useState("10");
    const [resolution, setResolution] = useState("1080p");
    const [aspectRatio, setAspectRatio] = useState("16:9");
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [activeJob, setActiveJob] = useState(null);
    const [videoDuration, setVideoDuration] = useState(0);
    const [videoStartTime, setVideoStartTime] = useState(0);
    const [videoEndTime, setVideoEndTime] = useState(0);
    const [videoThumbnails, setVideoThumbnails] = useState([]);
    const [dragTarget, setDragTarget] = useState(null);
    const timelineRef = useRef(null);
    const maxVideoSegment = 15;
    const selectedDuration = video
        ? Math.max(0, videoEndTime - videoStartTime)
        : Number(duration || 0);
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const submittingRef = useRef(false);
    const activeJobRef = useRef(null);
    const previewUrlsRef = useRef([]);

    const MAX_CHARACTERS = 4;

    const [selectedCharacters, setSelectedCharacters] = useState([]);
    const [selectionBox, setSelectionBox] = useState(null);
    const [isDrawingSelection, setIsDrawingSelection] = useState(false);

    const [isCharacterSelectionMode, setIsCharacterSelectionMode] = useState(false);
    const [currentCharacterTime, setCurrentCharacterTime] = useState(0);

    const characterVideoRef = useRef(null);
    const characterOverlayRef = useRef(null);
    const selectionStartRef = useRef(null);

    const credits = AIuser?.credits != null
        ? Math.floor(Number(AIuser.credits)).toLocaleString()
        : "0";
    const maxDuration = video ? 15 : 30;
    const selectedResolution = RESOLUTIONS.find(
        (item) => item.value === resolution
    );
    const creditsPerSecond = selectedResolution?.creditsPerSecond || 0;
    const estimatedCredits = creditsPerSecond * selectedDuration;
    const hasEnoughCredits =
        Number(AIuser?.credits || 0) >= estimatedCredits;

    /* =========================================================
       HELPERS
    ========================================================= */

    const updateActiveJob = (job) => {
        activeJobRef.current = job;
        setActiveJob(job);

        if (job) {
            localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(job));
        } else {
            localStorage.removeItem(ACTIVE_JOB_KEY);
        }
    };

    const normalizeResult = (payload, fallback = {}) => {
        const data =
            payload?.data ||
            payload?.session ||
            payload ||
            {};
        const videoUrl = data.videoUrl || data.outputUrl;

        if (!videoUrl) {
            return null;
        }

        let cost = "";

        if (data.cost != null) {
            cost = `$${Number(data.cost).toFixed(2)}`;
        } else if (data.costUsd != null) {
            cost = `$${Number(data.costUsd).toFixed(2)}`;
        } else if (data.creditsUsed != null) {
            cost = `${data.creditsUsed} credits`;
        }

        return {
            transactionId:
                data.transactionId ||
                fallback.transactionId ||
                "",

            sessionId:
                data.sessionId ||
                data._id ||
                fallback.sessionId ||
                "",
            videoUrl,
            cost,
            creditsUsed: data.creditsUsed,
            duration:
                data.duration ??
                fallback.duration ??
                Number(duration),
            resolution:
                data.resolution ||
                fallback.resolution ||
                resolution,
            aspectRatio:
                data.aspectRatio ||
                fallback.aspectRatio ||
                aspectRatio,
            status: data.status || "completed",
        };
    };

    const finishJob = (payload, fallback = {}) => {
        const currentJob =
            activeJobRef.current ||
            getStoredJob() ||
            fallback;
        const normalized = normalizeResult(payload, currentJob);
        if (!normalized?.videoUrl) {
            return false;
        }
        setResult(normalized);
        setIsLoading(false);
        setLoadingStep("");
        setErrorMessage("");
        submittingRef.current = false;
        updateActiveJob(null);
        if (typeof setUserRefetch === "function") {
            setUserRefetch((prev) => !prev);
        }
        return true;
    };

    const failJob = (message) => {
        const error =
            message ||
            "Motion video generation failed. Please try again.";
        setIsLoading(false);
        setLoadingStep("");
        setErrorMessage(error);
        submittingRef.current = false;
        updateActiveJob(null);
        swal({
            text: error,
            icon: "error",
            buttons: "Ok",
            className: "modal_class_success",
        });
    };

    /* =========================================================
       INITIALIZATION AND PREVIEW CLEANUP
    ========================================================= */

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        previewUrlsRef.current = [
            ...images.map((image) => image.preview),
            ...(video?.preview ? [video.preview] : []),
        ];
    }, [images, video]);

    useEffect(() => {
        return () => {
            previewUrlsRef.current.forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch {
                    // Ignore already-revoked preview URLs.
                }
            });
        };
    }, []);

    /* =========================================================
       WEBSOCKET EVENTS
       The global WebSocket manager should dispatch:
       backend_task_completed
       backend_task_failed

       Payload type: motion-video-ai
    ========================================================= */

    useEffect(() => {
        const getPayload = (event) => {
            const detail = event?.detail || {};
            if (detail.data && !detail.type) {
                return {
                    ...detail.data,
                    ...detail,
                };
            }
            return detail;
        };

        const matchesActiveJob = (payload) => {
            const job =
                activeJobRef.current ||
                getStoredJob();

            if (!job) {
                return false;
            }
            const type = String(
                payload?.type ||
                payload?.eventType ||
                ""
            ).toLowerCase();

            const transactionId =
                payload?.transactionId ||
                payload?.data?.transactionId;

            const sessionId =
                payload?.sessionId ||
                payload?.data?.sessionId ||
                payload?.data?._id;

            const isMotionEvent =
                !type ||
                [
                    "motion-video-ai",
                    "motion_video_ai",
                    "motion_video_ai_completed",
                    "motion_video_ai_failed",
                ].includes(type);

            if (!isMotionEvent) {
                return false;
            }

            if (
                job.transactionId &&
                transactionId
            ) {
                return job.transactionId === transactionId;
            }

            if (
                job.sessionId &&
                sessionId
            ) {
                return job.sessionId === sessionId;
            }

            return Boolean(
                type &&
                type.includes("motion")
            );
        };

        const handleCompletedEvent = (event) => {
            const payload = getPayload(event);
            if (!matchesActiveJob(payload)) {
                return;
            }
            const status = String(
                payload.status ||
                payload.data?.status ||
                ""
            ).toLowerCase();

            if (status === "failed") {
                failJob(payload.message);
                return;
            }
            if (
                status === "processing" ||
                status === "pending"
            ) {
                setLoadingStep(
                    payload.message ||
                    "Processing video reference and generating motion video..."
                );

                return;
            }

            const currentJob =
                activeJobRef.current ||
                getStoredJob();

            const completed = finishJob(
                payload,
                currentJob
            );

            if (!completed) {
                setLoadingStep(
                    payload.message ||
                    "Your video is being finalized..."
                );
            }
        };

        const handleFailedEvent = (event) => {
            const payload = getPayload(event);

            if (!matchesActiveJob(payload)) {
                return;
            }

            failJob(payload.message);
        };

        window.addEventListener(
            "backend_task_completed",
            handleCompletedEvent
        );

        window.addEventListener(
            "backend_task_failed",
            handleFailedEvent
        );

        return () => {
            window.removeEventListener(
                "backend_task_completed",
                handleCompletedEvent
            );

            window.removeEventListener(
                "backend_task_failed",
                handleFailedEvent
            );
        };
    }, [setUserRefetch]);

    /* =========================================================
       SESSION RECOVERY
       Checks the session endpoint after reload.
    ========================================================= */

    useEffect(() => {
        let cancelled = false;
        let timer;

        const restoreSession = () => {
            const stored = getStoredJob();

            if (!stored) {
                return;
            }
            activeJobRef.current = stored;
            setActiveJob(stored);
            setIsLoading(true);
            setLoadingStep(
                "Reconnecting to your motion video generation..."
            );
            submittingRef.current = true;
        };
        restoreSession();
        const storedJob =
            activeJobRef.current ||
            getStoredJob();
        if (!storedJob?.sessionId) {
            return () => {
                cancelled = true;
            };
        }

        const checkSession = async () => {
            try {
                const response = await fetch(
                    `${SESSION_ENDPOINT}/${encodeURIComponent(
                        storedJob.sessionId
                    )}`,
                    {
                        method: "GET",
                        headers: {
                            ...authHeader(),
                            Accept: "application/json",
                        },
                    }
                );

                const data = await response
                    .json()
                    .catch(() => null);

                if (
                    cancelled ||
                    !response.ok ||
                    !data?.status
                ) {
                    return;
                }

                const session =
                    data.session ||
                    data.data ||
                    {};

                const sessionStatus = String(
                    session.status || ""
                ).toLowerCase();

                if (
                    sessionStatus === "completed" ||
                    session.videoUrl
                ) {
                    finishJob(
                        session,
                        storedJob
                    );

                    return;
                }

                if (sessionStatus === "failed") {
                    failJob(
                        session.message ||
                        "Motion video generation failed."
                    );

                    return;
                }

                setLoadingStep(
                    session.message ||
                    "Processing video reference and generating motion video..."
                );
            } catch (error) {
                // Temporary network errors do not stop WebSocket processing.
            }
        };

        checkSession();

        timer = window.setInterval(
            checkSession,
            4000
        );

        const handleVisibility = () => {
            if (!document.hidden) {
                checkSession();
            }
        };

        window.addEventListener("focus", checkSession);
        window.addEventListener("pageshow", checkSession);

        document.addEventListener(
            "visibilitychange",
            handleVisibility
        );

        return () => {
            cancelled = true;

            if (timer) {
                window.clearInterval(timer);
            }

            window.removeEventListener(
                "focus",
                checkSession
            );

            window.removeEventListener(
                "pageshow",
                checkSession
            );

            document.removeEventListener(
                "visibilitychange",
                handleVisibility
            );
        };
    }, []);


    useEffect(() => {
        let cancelled = false;
        const loadThumbnails = async () => {
            if (!video?.file) {
                setVideoThumbnails([]);
                return;
            }
            const frames = await generateVideoThumbnails(video.file);

            if (!cancelled) {
                setVideoThumbnails(frames);
            }
        };
        loadThumbnails();
        return () => {
            cancelled = true;
        };
    }, [video]);

    /* =========================================================
       IMAGE HANDLERS
    ========================================================= */

    const handleImagesChange = (event) => {
        const files = Array.from(
            event.target.files || []
        );
        event.target.value = "";
        if (!files.length) {
            return;
        }
        const validFiles = files.filter((file) =>
            IMAGE_TYPES.includes(file.type)
        );
        if (validFiles.length !== files.length) {
            showAlert(
                "Only PNG, JPG, JPEG and WEBP images are supported."
            );
        }
        if (!validFiles.length) {
            return;
        }
        const slots = MAX_IMAGES - images.length;
        if (slots <= 0) {
            showAlert(
                `You can upload a maximum of ${MAX_IMAGES} reference images.`
            );

            return;
        }
        if (validFiles.length > slots) {
            showAlert(
                `You can upload a maximum of ${MAX_IMAGES} images. Only ${slots} more image(s) will be added.`
            );
        }
        const addedImages = validFiles
            .slice(0, slots)
            .map((file) => ({
                id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
                    .toString(36)
                    .slice(2, 8)}`,
                file,
                preview: URL.createObjectURL(file),
            }));

        setImages((previous) => [
            ...previous,
            ...addedImages,
        ]);
        setErrorMessage("");
    };
    const handleRemoveImage = (id, event) => {
        event.stopPropagation();
        setImages((previous) => {
            const target = previous.find(
                (image) => image.id === id
            );
            if (target?.preview) {
                URL.revokeObjectURL(target.preview);
            }
            return previous.filter(
                (image) => image.id !== id
            );
        });
    };

    /* =========================================================
       VIDEO HANDLERS
    ========================================================= */

    const handleVideoChange = (event) => {
        const files = Array.from(
            event.target.files || []
        );

        event.target.value = "";

        if (!files.length) {
            return;
        }

        if (files.length > 1) {
            showAlert(
                "Only one reference video is allowed. The first video will be used."
            );
        }

        const file = files[0];

        if (!VIDEO_TYPES.includes(file.type)) {
            showAlert(
                "Only MP4 and WEBM videos are supported."
            );

            return;
        }

        if (video?.preview) {
            URL.revokeObjectURL(video.preview);
        }


        selectedCharacters.forEach((character) => {
            if (character.preview) {
                URL.revokeObjectURL(character.preview);
            }
        });

        setSelectedCharacters([]);
        setSelectionBox(null);
        setIsDrawingSelection(false);
        setIsCharacterSelectionMode(false);
        setCurrentCharacterTime(0);

        setVideo({
            file,
            preview: URL.createObjectURL(file),
        });

        setVideoDuration(0);
        setVideoStartTime(0);
        setVideoEndTime(0);

        setErrorMessage("");

        // A video limits duration to 15 seconds.
        if (Number(duration) > 15) {
            setDuration("15");
        }
    };


    const updateTimelineTime = (event, target) => {
        const track = timelineRef.current;

        if (!track || !videoDuration) return;

        const rect = track.getBoundingClientRect();

        const ratio = Math.max(
            0,
            Math.min(1, (event.clientX - rect.left) / rect.width)
        );

        const nextTime = Math.round(ratio * videoDuration);

        if (target === "start") {
            const nextStart = Math.min(
                nextTime,
                videoDuration - 1
            );

            setVideoStartTime(nextStart);

            setVideoEndTime((currentEnd) =>
                Math.max(
                    nextStart + 1,
                    Math.min(
                        currentEnd,
                        nextStart + maxVideoSegment,
                        videoDuration
                    )
                )
            );
        }

        if (target === "end") {
            setVideoEndTime(
                Math.max(
                    videoStartTime + 1,
                    Math.min(
                        nextTime,
                        videoDuration,
                        videoStartTime + maxVideoSegment
                    )
                )
            );
        }
    };

    const handleTimelinePointerDown = (event, target) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragTarget(target);
        updateTimelineTime(event, target);
    };

    const handleTimelinePointerMove = (event) => {
        if (dragTarget) {
            updateTimelineTime(event, dragTarget);
        }
    };

    const handleTimelinePointerUp = () => {
        setDragTarget(null);
    };

    const handleCharacterSelectionStart = (event) => {
        if (
            !video ||
            isLoading ||
            selectedCharacters.length >= MAX_CHARACTERS
        ) {
            return;
        }

        const player = characterVideoRef.current;
        const overlay = characterOverlayRef.current;

        if (!player || !overlay) return;
        player.pause();

        const rect = overlay.getBoundingClientRect();

        const x = Math.max(
            0,
            Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)
        );

        const y = Math.max(
            0,
            Math.min(100, ((event.clientY - rect.top) / rect.height) * 100)
        );

        selectionStartRef.current = { x, y };

        setSelectionBox({
            x,
            y,
            width: 0,
            height: 0,
        });

        setIsDrawingSelection(true);

        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleCharacterSelectionMove = (event) => {
        if (!isDrawingSelection || !selectionStartRef.current) {
            return;
        }

        const overlay = characterOverlayRef.current;

        if (!overlay) return;

        const rect = overlay.getBoundingClientRect();

        const currentX = Math.max(
            0,
            Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)
        );

        const currentY = Math.max(
            0,
            Math.min(100, ((event.clientY - rect.top) / rect.height) * 100)
        );

        const start = selectionStartRef.current;

        setSelectionBox({
            x: Math.min(start.x, currentX),
            y: Math.min(start.y, currentY),
            width: Math.abs(currentX - start.x),
            height: Math.abs(currentY - start.y),
        });
    };

    const handleCharacterSelectionEnd = () => {
        setIsDrawingSelection(false);
        selectionStartRef.current = null;
    };

    const handleCancelCharacterSelection = () => {
        setSelectionBox(null);
        setIsDrawingSelection(false);
        selectionStartRef.current = null;
    };


    const handleMarkCharacter = () => {
        const player = characterVideoRef.current;
        const overlay = characterOverlayRef.current;

        if (!player || !overlay || !selectionBox) {
            showAlert("Please select a character from the video.");
            return;
        }

        if (selectedCharacters.length >= MAX_CHARACTERS) {
            showAlert(
                `You can select a maximum of ${MAX_CHARACTERS} characters.`
            );
            return;
        }

        if (
            selectionBox.width < 2 ||
            selectionBox.height < 2
        ) {
            showAlert("Please draw a larger selection around the character.");
            return;
        }

        const canvas = document.createElement("canvas");
        const sourceX = (selectionBox.x / 100) * player.videoWidth;
        const sourceY = (selectionBox.y / 100) * player.videoHeight;
        const sourceWidth = (selectionBox.width / 100) * player.videoWidth;
        const sourceHeight = (selectionBox.height / 100) * player.videoHeight;

        canvas.width = Math.round(sourceWidth);
        canvas.height = Math.round(sourceHeight);

        const context = canvas.getContext("2d");

        if (!context) {
            showAlert("Unable to process the selected character.");
            return;
        }

        try {
            context.drawImage(
                player,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0,
                0,
                canvas.width,
                canvas.height
            );

            canvas.toBlob((blob) => {
                if (!blob) {
                    showAlert("Could not crop the selected character.");
                    return;
                }

                const characterNumber = selectedCharacters.length + 1;

                const file = new File(
                    [blob],
                    `character-${characterNumber}.jpg`,
                    { type: "image/jpeg" }
                );

                const preview = URL.createObjectURL(blob);

                const character = {
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    name: `Character ${characterNumber}`,
                    file,
                    preview,
                    timestamp: Number(player.currentTime.toFixed(2)),
                    boundingBox: {
                        x: Number(selectionBox.x.toFixed(2)),
                        y: Number(selectionBox.y.toFixed(2)),
                        width: Number(selectionBox.width.toFixed(2)),
                        height: Number(selectionBox.height.toFixed(2)),
                    },
                };

                setSelectedCharacters((previous) => [
                    ...previous,
                    character,
                ]);

                setSelectionBox(null);
                setIsDrawingSelection(false);
                setIsCharacterSelectionMode(false);
                selectionStartRef.current = null;
            }, "image/jpeg", 0.95);
        } catch (error) {
            console.error("Character crop error:", error);
            showAlert("Failed to crop the selected character.");
        }
    };

    const handleRemoveCharacter = (id) => {
        setSelectedCharacters((previous) => {
            const character = previous.find((item) => item.id === id);

            if (character?.preview) {
                URL.revokeObjectURL(character.preview);
            }

            return previous.filter((item) => item.id !== id);
        });
    };

    const handleRemoveVideo = (event) => {
        event.stopPropagation();

        if (video?.preview) {
            URL.revokeObjectURL(video.preview);
        }

        setVideo(null);
        selectedCharacters.forEach((character) => {
            if (character.preview) {
                URL.revokeObjectURL(character.preview);
            }
        });

        setSelectedCharacters([]);
        setSelectionBox(null);
        setIsDrawingSelection(false);
        setIsCharacterSelectionMode(false);
        setCurrentCharacterTime(0);

        setVideoStartTime("0");
        setVideoDuration(0);
        setErrorMessage("");
        setVideoEndTime(0);
        setVideoThumbnails([]);

    };

    const handleDrop = (event, kind) => {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer?.files;

        if (!files?.length) {
            return;
        }

        const syntheticEvent = {
            target: {
                files,
                value: "",
            },
        };

        if (kind === "image") {
            handleImagesChange(syntheticEvent);
        } else {
            handleVideoChange(syntheticEvent);
        }
    };

    const preventDefault = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    /* =========================================================
       VALIDATION
    ========================================================= */

    const validate = () => {
        const trimmedPrompt = prompt.trim();

        if (!trimmedPrompt) {
            return "Please enter a prompt describing how the motion video should be made.";
        }
        if (images.length > MAX_IMAGES) {
            return `You can use a maximum of ${MAX_IMAGES} reference images.`;
        }
        const durationNumber = selectedDuration;
        const durationLimit = video ? maxVideoSegment : 30;
        if (
            !Number.isInteger(durationNumber) ||
            durationNumber < 1 ||
            durationNumber > durationLimit
        ) {
            return `Duration must be between 1 and ${durationLimit} seconds.`;
        }
        const imageRefs = [
            ...trimmedPrompt.matchAll(/@Image(\d+)/gi),
        ].map((match) => Number(match[1]));

        if (images.length === 0 && imageRefs.length > 0) {
            return "Your prompt references images, but no reference images were uploaded. Upload the images or remove the @Image references.";
        }

        const invalidImageRef = imageRefs.find(
            (number) =>
                number < 1 ||
                number > images.length
        );

        if (invalidImageRef) {
            return `Your prompt mentions @Image${invalidImageRef}, but you uploaded ${images.length} image(s).`;
        }

        const videoRefs = [
            ...trimmedPrompt.matchAll(/@Video(\d+)/gi),
        ].map((match) => Number(match[1]));

        if (videoRefs.some((number) => number !== 1)) {
            return "Only @Video1 can be used in the prompt.";
        }

        if (!video && videoRefs.length > 0) {
            return "Your prompt references @Video1, but no reference video was uploaded. Upload a video or remove the @Video reference.";
        }
        if (
            AIuser &&
            AIuser.credits !== undefined &&
            Number(AIuser.credits) < estimatedCredits
        ) {
            return "NO_CREDITS";
        }

        return "";
    };

    /* =========================================================
       GENERATE VIDEO
    ========================================================= */

    const handleGenerate = async () => {
        if (
            submittingRef.current ||
            isLoading
        ) {
            return;
        }
        setErrorMessage("");
        const validationError = validate();
        if (validationError === "NO_CREDITS") {
            swal({
                text: "You don't have enough credits! Please buy credits to continue.",
                icon: "error",
                buttons: "Buy",
                className: "modal_class_success",
            }).then((confirmed) => {
                if (confirmed) {
                    navigate("/purchase");
                }
            });

            return;
        }
        if (validationError) {
            setErrorMessage(validationError);

            swal({
                text: validationError,
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });

            return;
        }
        submittingRef.current = true;
        setIsLoading(true);
        setResult(null);
        setCopied(false);
        setLoadingStep("Checking your prompt...");

        try {
            /* -----------------------------------------------
               STEP 1: PROMPT CHECKER
            ----------------------------------------------- */
            const checkerResponse = await fetch(
                PROMPT_CHECK_ENDPOINT,
                {
                    method: "POST",
                    headers: {
                        ...authHeader(),
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },

                    body: JSON.stringify({
                        prompt: prompt.trim(),
                        aiService: "motion-video-ai",
                        videoCount: video ? 1 : 0,
                        // imageCount: images.length,
                        imageCount: images.length + selectedCharacters.length,
                    }),
                }
            );

            const checkerData = await checkerResponse
                .json()
                .catch(() => null);

            if (
                !checkerResponse.ok ||
                !checkerData ||
                checkerData.status === false ||
                checkerData.success === false
            ) {
                throw new Error(
                    checkerData?.message ||
                    checkerData?.error ||
                    "Prompt check failed. Please revise your prompt."
                );
            }

            /* -----------------------------------------------
               STEP 2: CREATE MOTION VIDEO

               Raw files are sent directly as multipart/form-data.
               There is no separate file-upload API call.

               Backend must parse multipart/form-data and handle
               imageUrls/videoUrls as file fields.
            ----------------------------------------------- */
            setLoadingStep(
                "Sending your files and starting motion video generation..."
            );
            const formData = new FormData();
            formData.append(
                "prompt",
                prompt.trim()
            );
            images.forEach((image) => {
                formData.append(
                    "imageUrls",
                    image.file
                );
            });

            // Send selected original characters separately
            selectedCharacters.forEach((character) => {
                formData.append(
                    "characterImages",
                    character.file
                );
            });

            // Send character metadata
            formData.append(
                "characters",
                JSON.stringify(
                    selectedCharacters.map((character, index) => ({
                        id: character.id,
                        name: `Character ${index + 1}`,
                        timestamp: character.timestamp,
                        boundingBox: character.boundingBox,
                    }))
                )
            );

            if (video?.file) {
                formData.append(
                    "videoUrls",
                    video.file
                );
            }
            formData.append("duration", String(selectedDuration));

            formData.append(
                "resolution",
                resolution
            );

            formData.append(
                "aspectRatio",
                aspectRatio
            );

            formData.append(
                "sessionName",
                prompt.trim().replace(/\s+/g, " ").slice(0, 70) ||
                "Motion Video Session"
            );
            if (video?.file) {
                formData.append("startTime", String(videoStartTime));
                formData.append("endTime", String(videoEndTime));
            }

            const response = await fetch(
                GENERATE_ENDPOINT,
                {
                    method: "POST",

                    headers: {
                        ...authHeader(),
                        Accept: "application/json",
                    },

                    // Do not set Content-Type manually.
                    // The browser adds the multipart boundary.
                    body: formData,
                }
            );

            const data = await response
                .json()
                .catch(() => null);

            if (
                !response.ok ||
                !data ||
                data.status !== true
            ) {
                throw new Error(
                    data?.message ||
                    data?.error ||
                    "Failed to start motion video generation."
                );
            }

            /* -----------------------------------------------
               STEP 3: STORE ACTIVE TRANSACTION
            ----------------------------------------------- */

            const job = {
                transactionId:
                    data.transactionId ||
                    data.data?.transactionId ||
                    "",

                sessionId:
                    data.sessionId ||
                    data.data?._id ||
                    data.data?.sessionId ||
                    "",

                // duration: Number(duration),
                duration: selectedDuration,
                resolution,
                aspectRatio,
                startedAt: new Date().toISOString(),
            };

            updateActiveJob(job);

            setLoadingStep(
                data.message ||
                "Processing video reference and generating motion video..."
            );

            /* -----------------------------------------------
               STEP 4: HANDLE IMMEDIATE RESULT IF AVAILABLE

               Normally, WebSocket sends the final result.
            ----------------------------------------------- */

            const immediateResult = normalizeResult(
                data.data || data,
                job
            );

            if (immediateResult?.videoUrl) {
                finishJob(
                    data.data || data,
                    job
                );
            }
        } catch (error) {
            console.error(
                "Motion Video AI error:",
                error?.message || error
            );

            const message =
                error?.message === "Failed to fetch"
                    ? "Network error. Please check your connection and try again."
                    : error?.message ||
                    "Something went wrong. Please try again.";

            setErrorMessage(message);
            setIsLoading(false);
            setLoadingStep("");
            submittingRef.current = false;

            swal({
                text: message,
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        }
    };

    /* =========================================================
       COPY RESULT LINK
    ========================================================= */

    const handleCopyLink = async () => {
        if (!result?.videoUrl) {
            return;
        }
        try {
            await navigator.clipboard.writeText(
                result.videoUrl
            );
            setCopied(true);
            window.setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (error) {
            showAlert(
                "Could not copy the link. Please copy it manually."
            );
        }
    };

    /* =========================================================
       CLEAR FORM
    ========================================================= */

    const handleClear = () => {
        if (isLoading) {
            return;
        }
        images.forEach((image) => {
            if (image.preview) {
                URL.revokeObjectURL(image.preview);
            }
        });
        if (video?.preview) {
            URL.revokeObjectURL(video.preview);
        }
        setImages([]);

        selectedCharacters.forEach((character) => {
            if (character.preview) {
                URL.revokeObjectURL(character.preview);
            }
        });

        setSelectedCharacters([]);
        setSelectionBox(null);
        setIsDrawingSelection(false);
        setIsCharacterSelectionMode(false);
        setCurrentCharacterTime(0);

        setVideo(null);
        setPrompt("");
        setDuration("10");
        setResolution("1080p");
        setAspectRatio("16:9");
        setErrorMessage("");
        setResult(null);
        setCopied(false);
        setVideoStartTime("0");
        setVideoDuration(0);
        setVideoEndTime(0);
        setVideoThumbnails([]);

        if (imageInputRef.current) {
            imageInputRef.current.value = "";
        }
        if (videoInputRef.current) {
            videoInputRef.current.value = "";
        }
    };

    const isFormReady = Boolean(
        prompt.trim() &&
        selectedDuration &&
        Number.isInteger(Number(selectedDuration)) &&
        Number(selectedDuration) >= 1 &&
        Number(selectedDuration) <= maxDuration
    );

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <Fragment>
            <div className="dance-video-ai-page">

                {/* Header */}
                <div className="dance-video-ai-top">
                    <p className="dance-video-ai-page-title">
                        MOTIONMORPH AI
                    </p>

                    <div className="credits-actions">
                        <button
                            type="button"
                            className="credits-button"
                        >
                            CREDITS : {credits}
                        </button>

                        <button
                            type="button"
                            className="buy-credits-button"
                            onClick={() => navigate("/purchase")}
                        >
                            BUY CREDITS
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/usage")}
                            className="money-exchange-button"
                            aria-label="View usage"
                        >
                            <img
                                src={moneyExchangeIcon}
                                alt="Usage"
                            />
                        </button>

                        <button
                            type="button"
                            className="money-exchange-button"
                            aria-label="See sample video"
                            onClick={() => {
                                // Add the sample-video action here when available.
                            }}
                        >
                            <img
                                src={sampleIcon}
                                alt="Sample video"
                            />
                        </button>
                    </div>
                </div>

                {/* Hero */}
                <section className="dance-video-ai-hero">
                    <h1 className="dance-video-ai-hero-title">
                        Transform Any Video.{" "}
                        <span className="dance-video-ai-gradient-text">
                            Reimagine Every Character.
                        </span>
                    </h1>

                    <p className="dance-video-ai-hero-sub">
                        Upload a motion-capture or reference video,
                        replace up to 3 characters with your chosen
                        characters, and preserve the original movement,
                        camera angles, and scene composition.
                    </p>
                </section>

                <div className="dance-video-ai-container">
                    <div className="dance-video-ai-section-head">
                        <h2 className="dance-video-ai-section-title">
                            Swap Up to{" "}
                            <span className="dance-video-ai-gradient-text">
                                3 Characters
                            </span>
                        </h2>

                        <p className="dance-video-ai-section-sub">
                            Upload optional reference images or a video,
                            describe the changes, then generate your
                            transformed video.
                        </p>
                    </div>

                    <div className="dance-video-ai-layout">
                        <div className="dance-video-ai-shell">

                            {/* STEP 1: VIDEO */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">
                                        1
                                    </span>

                                    <span className="dance-video-ai-step-title">
                                        Upload Motion Reference Video
                                    </span>
                                </div>

                                <label className="dance-video-ai-label">
                                    Driving Video{" "}
                                    <span className="dance-video-ai-label-note">
                                        (optional, one MP4 or WEBM video)
                                    </span>
                                </label>

                                <input
                                    ref={videoInputRef}
                                    id="dance-video-ai-video-input"
                                    type="file"
                                    accept="video/mp4,video/webm"
                                    onChange={handleVideoChange}
                                    className="dance-video-ai-hidden-input"
                                    disabled={isLoading}
                                />

                                {!video ? (
                                    <label
                                        htmlFor="dance-video-ai-video-input"
                                        className="dance-video-ai-upload"
                                        onDragOver={preventDefault}
                                        onDrop={(event) =>
                                            handleDrop(event, "video")
                                        }
                                    >
                                        <div className="dance-video-ai-upload-orb">
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.6"
                                                width="25"
                                                height="25"
                                            >
                                                <rect
                                                    x="3"
                                                    y="5"
                                                    width="13"
                                                    height="14"
                                                    rx="2.5"
                                                />

                                                <path d="m16 10 5-3v10l-5-3" />
                                            </svg>
                                        </div>

                                        <div className="dance-video-ai-upload-main">
                                            <span>Drop your video here</span>
                                            <span className="dance-video-ai-upload-browse">
                                                Browse
                                            </span>
                                        </div>

                                        <div className="dance-video-ai-upload-sub">
                                            Optional: use a video to transfer
                                            movement. Without a video, duration
                                            can be up to 30 seconds.
                                        </div>

                                        <div className="dance-video-ai-upload-meta">
                                            <span>MP4</span>
                                            <span>WEBM</span>
                                        </div>
                                    </label>
                                ) : (
                                    <div className="dance-video-ai-video-card">

                                        <video
                                            src={video.preview}
                                            controls
                                            playsInline
                                            preload="metadata"
                                            onLoadedMetadata={(event) => {
                                                const totalSeconds = event.currentTarget.duration;

                                                if (Number.isFinite(totalSeconds) && totalSeconds > 0) {
                                                    const total = Math.floor(totalSeconds);

                                                    setVideoDuration(total);
                                                    setVideoStartTime(0);
                                                    setVideoEndTime(Math.min(total, maxVideoSegment));
                                                }
                                            }}
                                        />
                                        <div className="dance-video-ai-video-bar">
                                            <span
                                                className="dance-video-ai-video-name"
                                                title={video.file.name}
                                            >
                                                @Video1 · {video.file.name}
                                            </span>

                                            <span className="dance-video-ai-video-actions">
                                                <label
                                                    htmlFor="dance-video-ai-video-input"
                                                    className="dance-video-ai-link-btn"
                                                >
                                                    Replace
                                                </label>

                                                <button
                                                    type="button"
                                                    className="dance-video-ai-link-btn"
                                                    onClick={handleRemoveVideo}
                                                    disabled={isLoading}
                                                >
                                                    Remove
                                                </button>
                                            </span>
                                        </div>

                                        {video && videoDuration > 0 && (
                                            <div className="dance-video-ai-start-time">
                                                <div className="dance-video-ai-selection-header">
                                                    <div>
                                                        <h4>Trim Reference Video</h4>
                                                        <p>Drag the yellow handles to select a video segment.</p>
                                                    </div>

                                                    <span className="dance-video-ai-duration-badge">
                                                        {selectedDuration}s
                                                    </span>
                                                </div>

                                                <div
                                                    className="dance-video-ai-timeline"
                                                    ref={timelineRef}
                                                    onPointerMove={handleTimelinePointerMove}
                                                    onPointerUp={handleTimelinePointerUp}
                                                    onPointerCancel={handleTimelinePointerUp}
                                                >
                                                    <div className="dance-video-ai-filmstrip">
                                                        {videoThumbnails.map((frame, index) => (
                                                            <img
                                                                key={index}
                                                                src={frame}
                                                                alt=""
                                                                draggable="false"
                                                            />
                                                        ))}
                                                    </div>

                                                    <div
                                                        className="dance-video-ai-timeline-dim left"
                                                        style={{
                                                            width: `${(videoStartTime / videoDuration) * 100}%`,
                                                        }}
                                                    />

                                                    <div
                                                        className="dance-video-ai-timeline-dim right"
                                                        style={{
                                                            left: `${(videoEndTime / videoDuration) * 100}%`,
                                                        }}
                                                    />

                                                    <div
                                                        className="dance-video-ai-selection-box"
                                                        style={{
                                                            left: `${(videoStartTime / videoDuration) * 100}%`,
                                                            width: `${((videoEndTime - videoStartTime) / videoDuration) * 100}%`,
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="dance-video-ai-timeline-handle start"
                                                            aria-label="Adjust start time"
                                                            onPointerDown={(event) =>
                                                                handleTimelinePointerDown(event, "start")
                                                            }
                                                            disabled={isLoading}
                                                        >
                                                            <span />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="dance-video-ai-timeline-handle end"
                                                            aria-label="Adjust end time"
                                                            onPointerDown={(event) =>
                                                                handleTimelinePointerDown(event, "end")
                                                            }
                                                            disabled={isLoading}
                                                        >
                                                            <span />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="dance-video-ai-selection-footer">
                                                    <span>Start: {videoStartTime}s</span>
                                                    <span>End: {videoEndTime}s</span>
                                                    <span>
                                                        Selected: {selectedDuration}s / {maxVideoSegment}s max
                                                    </span>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="dance-video-ai-link-btn"
                                                    disabled={isLoading}
                                                    onClick={() => {
                                                        setVideoStartTime(0);
                                                        setVideoEndTime(
                                                            Math.min(videoDuration, maxVideoSegment)
                                                        );
                                                    }}
                                                >
                                                    Reset selection
                                                </button>
                                            </div>
                                        )}



                                        {/* CHARACTER SELECTION */}
                                        {video && videoDuration > 0 && (
                                            <div className="dance-video-ai-character-section">

                                                <div className="dance-video-ai-selection-header">
                                                    <div>
                                                        <h4>Select Characters</h4>
                                                        <p>
                                                            Pause the video and draw a box around each
                                                            character you want to select.
                                                        </p>
                                                    </div>

                                                    <span className="dance-video-ai-duration-badge">
                                                        {selectedCharacters.length}/{MAX_CHARACTERS} Selected
                                                    </span>
                                                </div>

                                                {/* Character Selection Video */}
                                                <div className="dance-video-ai-character-player">

                                                    <div className="dance-video-ai-character-video-wrap">

                                                        <video
                                                            ref={characterVideoRef}
                                                            src={video.preview}
                                                            controls
                                                            playsInline
                                                            preload="metadata"
                                                            onLoadedMetadata={(event) => {
                                                                event.currentTarget.currentTime = videoStartTime;
                                                            }}
                                                            onTimeUpdate={(event) => {

                                                                const player = event.currentTarget;

                                                                setCurrentCharacterTime(player.currentTime);

                                                                // Keep selection inside the trimmed segment
                                                                if (player.currentTime > videoEndTime) {
                                                                    player.pause();
                                                                    player.currentTime = videoEndTime;
                                                                }

                                                                if (player.currentTime < videoStartTime) {
                                                                    player.currentTime = videoStartTime;
                                                                }
                                                            }}
                                                        />

                                                        {/* Selection Overlay */}
                                                        <div
                                                            ref={characterOverlayRef}
                                                            className={`dance-video-ai-character-overlay ${isCharacterSelectionMode
                                                                ? "is-active"
                                                                : ""
                                                                }`}
                                                            onPointerDown={handleCharacterSelectionStart}
                                                            onPointerMove={handleCharacterSelectionMove}
                                                            onPointerUp={handleCharacterSelectionEnd}
                                                            onPointerCancel={handleCharacterSelectionEnd}
                                                        >
                                                            {selectionBox && (
                                                                <div
                                                                    className="dance-video-ai-character-selection-box"
                                                                    style={{
                                                                        left: `${selectionBox.x}%`,
                                                                        top: `${selectionBox.y}%`,
                                                                        width: `${selectionBox.width}%`,
                                                                        height: `${selectionBox.height}%`,
                                                                    }}
                                                                >
                                                                    <span>
                                                                        Character Selection
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Selection Instructions */}
                                                    <div className="dance-video-ai-character-instructions">
                                                        {isCharacterSelectionMode ? (
                                                            <p>
                                                                <strong>Selection mode active:</strong>{" "}
                                                                Drag your mouse around a character to mark it.
                                                            </p>
                                                        ) : (
                                                            <p>
                                                                Pause the video at a frame where the character
                                                                is clearly visible, then start selecting.
                                                            </p>
                                                        )}

                                                        <span>
                                                            Current time:{" "}
                                                            {/* {characterVideoRef.current?.currentTime
                                                                ? `${characterVideoRef.current.currentTime.toFixed(2)}s`
                                                                : "0.00s"} */}
                                                            {currentCharacterTime.toFixed(2)}s
                                                        </span>
                                                    </div>

                                                    {/* Selection Actions */}
                                                    <div className="dance-video-ai-character-actions">

                                                        <button
                                                            type="button"
                                                            className="dance-video-ai-character-select-btn"
                                                            onClick={() => {
                                                                const player = characterVideoRef.current;

                                                                if (!player) return;

                                                                if (
                                                                    selectedCharacters.length >= MAX_CHARACTERS
                                                                ) {
                                                                    showAlert(
                                                                        `You can select a maximum of ${MAX_CHARACTERS} characters.`
                                                                    );
                                                                    return;
                                                                }

                                                                player.pause();
                                                                setSelectionBox(null);
                                                                setIsCharacterSelectionMode(true);
                                                            }}
                                                            disabled={
                                                                isLoading ||
                                                                selectedCharacters.length >= MAX_CHARACTERS
                                                            }
                                                        >
                                                            {isCharacterSelectionMode
                                                                ? "Selection Mode Active"
                                                                : "Select Character"}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="dance-video-ai-character-mark-btn"
                                                            onClick={handleMarkCharacter}
                                                            disabled={
                                                                isLoading ||
                                                                !selectionBox ||
                                                                !isCharacterSelectionMode
                                                            }
                                                        >
                                                            Mark Character
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="dance-video-ai-character-cancel-btn"
                                                            onClick={() => {
                                                                handleCancelCharacterSelection();
                                                                setIsCharacterSelectionMode(false);
                                                            }}
                                                            disabled={!selectionBox && !isCharacterSelectionMode}
                                                        >
                                                            Cancel
                                                        </button>

                                                    </div>
                                                </div>

                                                {/* Selected Character Cards */}
                                                <div className="dance-video-ai-selected-characters">

                                                    <div className="dance-video-ai-selected-header">
                                                        <h4>Marked Characters</h4>
                                                        <span>
                                                            {selectedCharacters.length} / {MAX_CHARACTERS}
                                                        </span>
                                                    </div>

                                                    {selectedCharacters.length === 0 ? (
                                                        <div className="dance-video-ai-character-empty">
                                                            <p>No characters selected yet.</p>
                                                            <span>
                                                                Select a character from the video and click
                                                                Mark Character.
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="dance-video-ai-character-grid">

                                                            {selectedCharacters.map((character, index) => (
                                                                <div
                                                                    className="dance-video-ai-character-card"
                                                                    key={character.id}
                                                                >
                                                                    <div className="dance-video-ai-character-image">
                                                                        <img
                                                                            src={character.preview}
                                                                            alt={character.name}
                                                                        />

                                                                        <span>
                                                                            {character.name}
                                                                        </span>
                                                                    </div>

                                                                    <div className="dance-video-ai-character-card-info">
                                                                        <strong>
                                                                            Character {index + 1}
                                                                        </strong>

                                                                        <span>
                                                                            {character.timestamp}s
                                                                        </span>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        className="dance-video-ai-character-remove"
                                                                        onClick={() =>
                                                                            handleRemoveCharacter(character.id)
                                                                        }
                                                                        disabled={isLoading}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            ))}

                                                        </div>
                                                    )}

                                                    {selectedCharacters.length >= MAX_CHARACTERS && (
                                                        <p className="dance-video-ai-character-limit">
                                                            Maximum {MAX_CHARACTERS} characters selected.
                                                        </p>
                                                    )}

                                                </div>
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>

                            {/* STEP 2: IMAGES */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">
                                        2
                                    </span>

                                    <span className="dance-video-ai-step-title">
                                        Upload Reference Images
                                    </span>
                                </div>

                                <label className="dance-video-ai-label">
                                    Character Images{" "}
                                    <span className="dance-video-ai-label-note">
                                        (optional, up to {MAX_IMAGES} images)
                                    </span>
                                </label>

                                <input
                                    ref={imageInputRef}
                                    id="dance-video-ai-image-input"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    multiple
                                    onChange={handleImagesChange}
                                    className="dance-video-ai-hidden-input"
                                    disabled={isLoading}
                                />

                                {images.length === 0 ? (
                                    <label
                                        htmlFor="dance-video-ai-image-input"
                                        className="dance-video-ai-upload"
                                        onDragOver={preventDefault}
                                        onDrop={(event) =>
                                            handleDrop(event, "image")
                                        }
                                    >
                                        <div className="dance-video-ai-upload-orb">
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.6"
                                                width="25"
                                                height="25"
                                            >
                                                <rect
                                                    x="3"
                                                    y="3"
                                                    width="18"
                                                    height="18"
                                                    rx="3"
                                                />
                                                <circle
                                                    cx="8.5"
                                                    cy="8.5"
                                                    r="1.5"
                                                />
                                                <path d="m21 15-5-5L5 21" />
                                            </svg>
                                        </div>

                                        <div className="dance-video-ai-upload-main">
                                            <span>Drop your images here</span>
                                            <span className="dance-video-ai-upload-browse">
                                                Browse
                                            </span>
                                        </div>

                                        <div className="dance-video-ai-upload-sub">
                                            Optional: add clear character
                                            reference photos.
                                        </div>

                                        <div className="dance-video-ai-upload-meta">
                                            <span>JPG</span>
                                            <span>PNG</span>
                                            <span>WEBP</span>
                                        </div>
                                    </label>
                                ) : (
                                    <div className="dance-video-ai-image-grid">
                                        {images.map((image, index) => (
                                            <div
                                                className="dance-video-ai-image-card"
                                                key={image.id}
                                            >
                                                <img
                                                    src={image.preview}
                                                    alt={`Reference ${index + 1}`}
                                                />

                                                <span className="dance-video-ai-image-number">
                                                    @Image{index + 1}
                                                </span>

                                                <button
                                                    type="button"
                                                    className="dance-video-ai-remove"
                                                    onClick={(event) =>
                                                        handleRemoveImage(
                                                            image.id,
                                                            event
                                                        )
                                                    }
                                                    aria-label={`Remove image ${index + 1}`}
                                                    disabled={isLoading}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}

                                        {images.length < MAX_IMAGES && (
                                            <label
                                                htmlFor="dance-video-ai-image-input"
                                                className="dance-video-ai-image-add"
                                                onDragOver={preventDefault}
                                                onDrop={(event) =>
                                                    handleDrop(event, "image")
                                                }
                                            >
                                                <span className="dance-video-ai-image-add-plus">
                                                    +
                                                </span>

                                                <span>Add image</span>
                                            </label>
                                        )}
                                    </div>
                                )}

                                <div className="dance-video-ai-count">
                                    {images.length}/{MAX_IMAGES} images uploaded
                                </div>
                            </div>



                            {/* STEP 3: PROMPT */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">
                                        3
                                    </span>

                                    <span className="dance-video-ai-step-title">
                                        Enter Your Prompt
                                    </span>
                                </div>

                                <div className="dance-video-ai-label-row">
                                    <label
                                        className="dance-video-ai-label"
                                        htmlFor="dance-video-ai-prompt"
                                    >
                                        Enter Prompt{" "}
                                        <span className="dance-video-ai-required">
                                            *
                                        </span>
                                    </label>

                                    <button
                                        type="button"
                                        className="dance-video-ai-clear"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                    >
                                        Clear
                                    </button>
                                </div>

                                <div className="dance-video-ai-prompt-wrap">
                                    <textarea
                                        id="dance-video-ai-prompt"
                                        className="dance-video-ai-textarea"
                                        value={prompt}
                                        maxLength={2000}
                                        onChange={(event) =>
                                            setPrompt(event.target.value)
                                        }
                                        placeholder="Describe the video you want to generate. You can reference @Image1, @Image2, @Image3 and @Video1 when those files are uploaded."
                                        disabled={isLoading}
                                    />

                                    <span className="dance-video-ai-prompt-count">
                                        {prompt.length}/2000
                                    </span>
                                </div>

                                <p className="dance-video-ai-hint">
                                    <span className="dance-video-ai-hint-label">
                                        How to reference files:
                                    </span>{" "}
                                    use <code>@Image1</code>,{" "}
                                    <code>@Image2</code>,{" "}
                                    <code>@Image3</code> for images in upload
                                    order and <code>@Video1</code> for the
                                    video. References are optional.

                                    {" "}

                                    <button
                                        type="button"
                                        className="dance-video-ai-example-btn"
                                        onClick={() =>
                                            setPrompt(DEFAULT_PROMPT_EXAMPLE)
                                        }
                                        disabled={isLoading}
                                    >
                                        Use example
                                    </button>
                                </p>
                            </div>

                            {/* STEP 4: SETTINGS */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">
                                        4
                                    </span>

                                    <span className="dance-video-ai-step-title">
                                        Configure Video Settings
                                    </span>
                                </div>

                                <div className="dance-video-ai-settings">
                                    <div className="dance-video-ai-setting-card">
                                        <label
                                            className="dance-video-ai-label"
                                            htmlFor="dance-video-ai-duration"
                                        >
                                            Duration (Seconds){" "}
                                            <span className="dance-video-ai-required">
                                                *
                                            </span>

                                            <span className="dance-video-ai-label-note">
                                                {" "}(max {maxDuration}s)
                                            </span>
                                        </label>
                                        {video ? (
                                            <div className="dance-video-ai-number "
                                                style={{
                                                    paddingTop: "11px"
                                                }}
                                            >
                                                {selectedDuration}
                                            </div>
                                        ) : (
                                            <input
                                                id="dance-video-ai-duration"
                                                type="number"
                                                min="1"
                                                max={30}
                                                step="1"
                                                inputMode="numeric"
                                                className="dance-video-ai-number"
                                                value={duration}
                                                onChange={(event) => setDuration(event.target.value)}
                                                disabled={isLoading}
                                            />
                                        )}

                                    </div>

                                    <div className="dance-video-ai-setting-card">
                                        <label className="dance-video-ai-label">
                                            Resolution{" "}
                                            <span className="dance-video-ai-required">
                                                *
                                            </span>
                                        </label>

                                        <div className="dance-video-ai-chips">
                                            {RESOLUTIONS.map((item) => (
                                                <button
                                                    type="button"
                                                    key={item.value}
                                                    className={`dance-video-ai-chip ${resolution === item.value
                                                        ? "is-selected"
                                                        : ""
                                                        }`}
                                                    onClick={() =>
                                                        setResolution(item.value)
                                                    }
                                                    disabled={isLoading}
                                                >
                                                    <span className="dance-video-ai-chip-top">
                                                        {item.tag}
                                                    </span>

                                                    <span className="dance-video-ai-chip-bottom">
                                                        {item.label}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="dance-video-ai-setting-card">
                                        <label className="dance-video-ai-label">
                                            Aspect Ratio{" "}
                                            <span className="dance-video-ai-required">
                                                *
                                            </span>
                                        </label>

                                        <div className="dance-video-ai-chips">
                                            {ASPECTS.map((item) => (
                                                <button
                                                    type="button"
                                                    key={item.value}
                                                    className={`dance-video-ai-chip ${aspectRatio === item.value
                                                        ? "is-selected"
                                                        : ""
                                                        }`}
                                                    onClick={() =>
                                                        setAspectRatio(item.value)
                                                    }
                                                    disabled={isLoading}
                                                >
                                                    <span className="dance-video-ai-chip-top">
                                                        {item.value}
                                                    </span>

                                                    <span className="dance-video-ai-chip-bottom">
                                                        {item.label}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* GENERATE BAR */}
                            <div className="dance-video-ai-generate-bar">
                                <div className="dance-video-ai-generate-info">
                                    <span className="dance-video-ai-generate-label">
                                        Estimated Cost
                                    </span>
                                    <span className="dance-video-ai-generate-value">
                                        {estimatedCredits.toLocaleString()} Credits
                                    </span>
                                </div>
                                <div className="dance-video-ai-generate-actions">
                                    <button
                                        type="button"
                                        className="dance-video-ai-clear"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                    >
                                        Clear
                                    </button>

                                    <button
                                        type="button"
                                        className="dance-video-ai-generate-btn"
                                        onClick={handleGenerate}
                                        disabled={
                                            !isFormReady ||
                                            isLoading ||
                                            !hasEnoughCredits
                                        }
                                    >
                                        {isLoading ? (
                                            <span
                                                className="dance-video-ai-spinner"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <AutoAwesomeIcon fontSize="small" />
                                        )}

                                        {isLoading
                                            ? "Processing..."
                                            : "Generate Video"}
                                    </button>
                                </div>

                            </div>
                            {!hasEnoughCredits && (
                                <p className="dance-video-ai-error mt-2">
                                    You need {estimatedCredits.toLocaleString()} credits,
                                    but you have {Number(AIuser?.credits || 0).toLocaleString()}.
                                </p>
                            )}

                            <p className="dance-video-ai-disclaimer">
                                AI-generated content may occasionally contain
                                mistakes. Please verify important information.
                            </p>

                            {/* ERROR */}
                            {errorMessage && !isLoading && (
                                <div
                                    className="dance-video-ai-error"
                                    role="alert"
                                >
                                    {errorMessage}
                                </div>
                            )}

                            {/* LOADING */}
                            {isLoading && (
                                <div
                                    className="dance-video-ai-progress"
                                    role="status"
                                >
                                    <div className="dance-video-ai-progress-orb">
                                        <AutoAwesomeIcon />
                                    </div>

                                    <p className="dance-video-ai-progress-title">
                                        {loadingStep ||
                                            "Generating your motion video..."}
                                    </p>

                                    <p className="dance-video-ai-progress-text">
                                        This can take a few minutes. Your
                                        generation status will update when the
                                        backend sends a WebSocket event.
                                    </p>
                                </div>
                            )}
                            {!isLoading && result && (
                                <div className="dance-video-ai-result">
                                    <div className="dance-video-ai-result-badge">
                                        <span />
                                        Generation Complete
                                    </div>

                                    <h4 className="dance-video-ai-result-title">
                                        Your video is ready
                                    </h4>

                                    <div className="dance-video-ai-result-player">
                                        <video
                                            src={result.videoUrl}
                                            controls
                                            autoPlay
                                            muted
                                            playsInline
                                        />
                                    </div>

                                    <div className="dance-video-ai-result-grid">
                                        <div className="dance-video-ai-result-item">
                                            <span>Duration</span>
                                            <strong>
                                                {result.duration
                                                    ? `${result.duration}s`
                                                    : "—"}
                                            </strong>
                                        </div>

                                        <div className="dance-video-ai-result-item">
                                            <span>Resolution</span>
                                            <strong>
                                                {result.resolution || "—"}
                                            </strong>
                                        </div>

                                        <div className="dance-video-ai-result-item">
                                            <span>Aspect Ratio</span>
                                            <strong>
                                                {result.aspectRatio || "—"}
                                            </strong>
                                        </div>

                                        <div className="dance-video-ai-result-item">
                                            <span>Cost</span>
                                            <strong>
                                                {result.cost ?? "—"}
                                            </strong>
                                        </div>

                                        <div className="dance-video-ai-result-item">
                                            <span>Status</span>
                                            <strong>
                                                {result.status || "completed"}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className="dance-video-ai-link-row">
                                        <button
                                            type="button"
                                            className="dance-video-ai-download-btn"
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(result.videoUrl);

                                                    if (!response.ok) {
                                                        throw new Error("Failed to download video.");
                                                    }

                                                    const blob = await response.blob();
                                                    const blobUrl = window.URL.createObjectURL(blob);

                                                    const link = document.createElement("a");
                                                    link.href = blobUrl;
                                                    link.download = `dance-video-${Date.now()}.mp4`;

                                                    document.body.appendChild(link);
                                                    link.click();
                                                    link.remove();

                                                    window.URL.revokeObjectURL(blobUrl);
                                                } catch (error) {
                                                    console.error("Video download failed:", error);

                                                    // Fallback: open the video URL if direct download fails
                                                    window.open(
                                                        result.videoUrl,
                                                        "_blank",
                                                        "noopener,noreferrer"
                                                    );
                                                }
                                            }}
                                        >
                                            <icon name="download" size="sm" />
                                            Download Video
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}